import { useCallback, useMemo } from 'react';
import { useSchedule, useScheduleActions, type Schedule } from './queries';

export interface SavedSessionsValue {
  ids: ReadonlySet<string>;
  isSaved: (id: string) => boolean;
  toggle: (id: string) => void;
  /** The schedule itself, for the screen that renders it rather than merely counts it. */
  schedule: Schedule | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  /** A star is in flight. The screens do not wait for it — see below — but they may say so. */
  isSaving: boolean;
  /** The last star did not land, so the one on screen is not the one on the server. */
  saveFailed: boolean;
}

/**
 * SCH-01: a saved session belongs to the woman, not to the browser she happened to star it in. So
 * this reads `/api/schedule` rather than local storage — she stars three talks on the shuttle and
 * they are waiting on the laptop in her room.
 *
 * No sign-in check: every screen in this app is behind the gate in `App`, so anything that calls
 * this already has a session.
 *
 * The stars are optimistic. A phone in a conference hall takes a moment to answer and a star that
 * stays dark for that moment reads as a tap that missed, so the pending variables are folded over
 * the server's answer and reconciled when the response lands.
 */
export function useSavedSessions(): SavedSessionsValue {
  const schedule = useSchedule();
  const { save, remove } = useScheduleActions();

  const ids = useMemo(() => {
    const next = new Set(schedule.data?.savedSessionIds ?? []);

    // Folded over the server's answer rather than replacing it, so a tap while the previous one is
    // still in the air lands on the state she can actually see.
    if (save.isPending && save.variables !== undefined) {
      next.add(save.variables);
    }
    if (remove.isPending && remove.variables !== undefined) {
      next.delete(remove.variables);
    }

    return next;
  }, [schedule.data, save.isPending, save.variables, remove.isPending, remove.variables]);

  const toggle = useCallback(
    (id: string) => {
      if (ids.has(id)) {
        remove.mutate(id);
      } else {
        save.mutate(id);
      }
    },
    [ids, save, remove],
  );

  const refetch = useCallback(() => void schedule.refetch(), [schedule]);

  return {
    ids,
    isSaved: (id: string) => ids.has(id),
    toggle,
    schedule: schedule.data,
    isPending: schedule.isPending,
    isError: schedule.isError,
    refetch,
    isSaving: save.isPending || remove.isPending,
    saveFailed: save.isError || remove.isError,
  };
}
